import {Component, Input, OnInit} from '@angular/core';
import {
  NutritionFacts,
  ProductVariation,
  Restaurant,
  RestaurantProduct,
  WorkDay
} from '../../../core/models/restaurant.model';
import * as moment from 'moment';
import { FormatDataService } from '../../../core/services/format-data.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ProductCollapseState } from '../../../core/models/cart-item.model';
import { UtilService } from '../../../core/services/util.service';
import { IMG_200, IMG_500 } from '../../../core/constants';
import {getPriceRange, getPriceWithModifiers} from '../../../core/dto/item.dto';
import {isModifierSoldOut} from '../../../core/dto/restaurant.dto';
import {DIETARY_LABEL, DietaryLabel} from '../../../core/constants/item.const';
import { CartStore } from 'src/app/core/stores/cart.store';
import { CustomerStore } from 'src/app/core/stores/customer.store';
import { CustomerFavoriteItemDTO } from 'src/app/core/models/customer.model';

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  animations: [
    trigger('changeProductCollapseState', [
      state(ProductCollapseState.HIDE, style({
        minHeight: 0,
        height: 0
      })),
      state(ProductCollapseState.SHOW, style({
        minHeight: '14rem'
      })),
      transition(`${ProductCollapseState.SHOW} => ${ProductCollapseState.HIDE}`, animate(300)),
      transition(`${ProductCollapseState.HIDE} => ${ProductCollapseState.SHOW}`, animate(200))
    ]),
  ]
})
export class ProductDetailsComponent implements OnInit{
  @Input() product: RestaurantProduct;
  @Input() chosenVariation: ProductVariation;
  @Input() hasPadding = false;
  @Input() hasAllBorderRadius = false;
  @Input() truncateDescription = false;
  @Input() truncateLength = 2;
  @Input() enforceInventory: boolean;
  @Input() hoursOfOperation: WorkDay[];
  @Input() currencySign = '$';
  @Input() enlargedImage: boolean;
  @Input() hidePrice: boolean;
  @Input() productCollapseState = ProductCollapseState.SHOW;
  @Input() restaurant: Restaurant = new Restaurant();
  @Input() isFavorite: boolean = false;
  @Input() orderBotUserId:string;
  @Input() enableFavorite: boolean = false;

  public getPriceWithModifiers = getPriceWithModifiers;
  public getPriceRange = getPriceRange;

  constructor(
    private formatDataService: FormatDataService,
    private utilService: UtilService,
    public cartStore: CartStore,
    private customerStore: CustomerStore
  ) {}

  ngOnInit() {
  }

  public get isProductHidden(): boolean {
    return this.productCollapseState === ProductCollapseState.HIDE;
  }

  public get productDescription(): string {
    return this.product.info.description;
  }

  public get isEnlargedImage(): boolean {
    return this.enlargedImage && this.product.info.imageUrl && this.product.info.imageUrl.length > 0;
  }

  public get formattedProductDescription(): string {
    const description = this.product.info.description.replace(/<br\s*\/?>/gi,' ');
    const tmp = document.createElement('div');
    tmp.innerHTML = description;
    return tmp.textContent || tmp.innerText || '';
  }

  public get isStoneCreek(): boolean {
    return this.utilService.isStoneCreek();
  }

  public getQuantity(product: RestaurantProduct): number {
    let quantity = 0;
    for (const variation of product.variations) {
      if (variation.quantityRemaining === null) {
        return Number.MAX_VALUE;
      } else if (variation.quantityRemaining > 0) {
        quantity += variation.quantityRemaining;
      }
    }
    return quantity;
  }

  public get isSpiceKitchen(): boolean {
    return this.product.locationId === 'TWH680W79EF31';
  }

  public isSoldOut(product: RestaurantProduct): boolean {
    return (this.enforceInventory && this.getQuantity(product) <= 0) || this.allSoldOut(product) || isModifierSoldOut(product);
  }

  public allSoldOut(product: RestaurantProduct): boolean {
    return product.variations.filter(variation => !variation.soldOut || (variation.trackInventory && variation.quantityRemaining > 0)).length === 0;
  }

  public isDisplayKyooFee():boolean {
    return this.restaurant && this.restaurant.displayKyooFeeOnMenu && this.cartStore && this.cartStore.totalQuantity == 0;
  }

  public get availableFromUntilText(): string {
    return this.formatDataService.availableFromUntilText(this.chosenVariation.availableFrom, this.chosenVariation.availableUntil, false);
  }

  public get availableText(): string {
    if (this.chosenVariation == null) {
      return '';
    }
    if (this.chosenVariation.isDailyRestrictionEnabled && this.chosenVariation.isFulfillmentRestrictionEnabled) {
      return `Available ${this.availableFulfillments.length > 0 ? 'for ' + this.availableFulfillments.toLowerCase() : ''} ${this.availableDays.length > 0 ? 'on ' + this.availableDays : ''}`;
    } else if (this.chosenVariation.isDailyRestrictionEnabled) {
      return `Available on ${this.availableDays}`;
    } else if (this.chosenVariation.isFulfillmentRestrictionEnabled) {
      return `Available for ${this.availableFulfillments.toLowerCase()}`;
    } else {
     return '';
    }
  }

  public get imageUrl(): string {
    const dimension = this.isEnlargedImage ? IMG_500 : IMG_200;
    return this.utilService.getImageUrl(this.product.info.imageUrl, dimension, this.isSpiceKitchen);
  }

  public get availableDays(): string {
    return this.formatDataService.getFormattedDailyAvailabilityText(this.chosenVariation.dailyAvailability);
  }

  public get availableFulfillments(): string {
    return this.formatDataService.getFormattedAvailableFulfillments(this.chosenVariation.availableInFulfillments);
  }

  public get variationLeadTime(): string {
    const nextOpenRange: moment.Moment[] = this.nextOpenRange();
    if (nextOpenRange.length < 1) {
      return '';
    }

    let leadTimesText = '';

    const [nextOpeningTime, nextClosingTime] = nextOpenRange;

    let cutoffDate: moment.Moment;
    let daysDifference: number;
    let minimumLeadTimeType: String = '';

    const openingLeadTime: moment.Duration = moment.duration(this.chosenVariation.openingLeadTime);
    if (openingLeadTime != null && openingLeadTime.asMinutes() > 0 && nextOpeningTime) {
      const openingCutoffDate: moment.Moment = nextOpeningTime.clone().subtract(openingLeadTime);
      daysDifference = nextOpeningTime.clone().startOf('day').diff(openingCutoffDate.clone().startOf('day'), 'days');
      cutoffDate = openingCutoffDate;
      minimumLeadTimeType = 'OPENING';
    }

    const closingLeadTime: moment.Duration = moment.duration(this.chosenVariation.closingLeadTime);
    if (closingLeadTime != null && closingLeadTime.asMinutes() > 0 && nextClosingTime) {
      const closingCutoffDate: moment.Moment = nextClosingTime.clone().subtract(closingLeadTime);
      if (closingCutoffDate < cutoffDate) {
        cutoffDate = closingCutoffDate;
        daysDifference = nextClosingTime.clone().startOf('day').diff(closingCutoffDate.clone().startOf('day'), 'days');
        minimumLeadTimeType = 'CLOSING';
      }
    }

    if (cutoffDate != null) {
      leadTimesText = 'Order cutoff:';
      if (daysDifference === 0) {
        leadTimesText += ` ${cutoffDate.format('h:mma').replace(':00','').replace('12am','Midnight before').replace('12pm','Noon')} `;
        leadTimesText += minimumLeadTimeType === 'OPENING' ? 'the day of pickup.' : `today (at least ${closingLeadTime.humanize()} before closing).`;
      } else if (daysDifference === 1) {
        leadTimesText += ` ${cutoffDate.format('h:mma').replace(':00','').replace('12am','Midnight').replace('12pm','Noon')} the day before pickup.`;
      } else {
        leadTimesText += ` ${cutoffDate.format('h:mma').replace(':00','')} at least ${daysDifference} day(s) before pickup.`;
      }
    }

    const pickupLeadTime: moment.Duration = moment.duration(this.chosenVariation.pickupLeadTime);
    if (pickupLeadTime && pickupLeadTime.asMinutes() > 0) {
      leadTimesText += ` Order must be placed at least ${pickupLeadTime.humanize()} in advance.`;
    }

    return leadTimesText;
  }

  public getNutritionFacts(facts: NutritionFacts): string {
    const entries = [
      facts.calories != null ? `calories ${facts.calories}kcal` : '',
      facts.total_fat != null ? `total fat ${facts.total_fat}g` : '',
      facts.saturated_fat != null ? `saturated fat ${facts.saturated_fat}g` : '',
      facts.trans_fat != null ? `trans fat ${facts.trans_fat}g` : '',
      facts.cholesterol != null ? `cholest. ${facts.cholesterol}mg` : '',
      facts.sodium != null ? `sodium ${facts.sodium}mg` : '',
      facts.potassium != null ? `potassium ${facts.potassium}mg` : '',
      facts.total_carbs != null ? `total carb. ${facts.total_carbs}g` : '',
      facts.fiber != null ? `fiber ${facts.fiber}g` : '',
      facts.sugars != null ? `sugars ${facts.sugars}g` : '',
      facts.added_sugars != null ? `added sugar ${facts.added_sugars}g` : '',
      facts.protein != null ? `protein ${facts.protein}g` : ''
    ];
    return entries.filter(entry => entry !== '').join(', ');
  }

  public nextOpenRange(): moment.Moment[] {
    if (this.hoursOfOperation) {
      const currentDate = new Date();
      const isBeforeOpen = moment().isBefore(moment(this.hoursOfOperation[currentDate.getDay()].startTime[0].getDate(currentDate)));

      if (this.hoursOfOperation[currentDate.getDay()].enable && isBeforeOpen) {
        return [
          moment(this.hoursOfOperation[currentDate.getDay()].startTime[0].getDate(currentDate)),
          moment(this.hoursOfOperation[currentDate.getDay()].endTime[0].getDate(currentDate)),
        ];
      } else {
        for (let i = 0; i < 7; i++) {
          currentDate.setDate(currentDate.getDate() + 1);
          const isEnabled = this.hoursOfOperation[currentDate.getDay()].enable;
          if (isEnabled) {
            return [
              moment(this.hoursOfOperation[currentDate.getDay()].startTime[0].getDate(currentDate)),
              moment(this.hoursOfOperation[currentDate.getDay()].endTime[0].getDate(currentDate))
            ];
          }
        }
      }
    }
    return [];
  }

  public getFormattedPrice(price: string | number, quantity = 1): string {
    if(this.isDisplayKyooFee()){
      return ((+price * quantity) + (this.restaurant.kyooFee/100)).toFixed(2);
    }
    return (+price * quantity).toFixed(2);
  }

  cleanedName(name: string): string {
    return name.replace('(*)', '').replace('(#)', '').replace('(^)', '').trim();
  }

  public toggleHeart(event: Event) {
    event.stopPropagation();
    if(this.orderBotUserId && !this.isFavorite){
      this.customerStore.addFavoriteItem(this.orderBotUserId, new CustomerFavoriteItemDTO(this.product.info.id))
    } else if(this.orderBotUserId){
      this.customerStore.removeFavoriteItem(this.orderBotUserId, this.product.info.id)
    } else {
      this.customerStore.toggleFavoriteItem(this.product.info.id)
    }
  }
}
